import { useState, useEffect } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import SideBar from "../components/SideBar";
import TopBar from "../components/TopBar";
import add from "../assets/image/add.svg";
import RSVPModal from "../components/RSVPModal";

export default function Events() {
  const [rsvpOpen, setRsvpOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  
  const toISODate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const clean = dateStr
        .replace(/(\d+)(st|nd|rd|th)/, "$1")
        .replace(",", "");
      const parsed = new Date(clean);
      if (isNaN(parsed)) return null;
      return parsed.toISOString().split("T")[0];
    } catch {
      return null;
    }
  };

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      setError("");

      try {
        const token = localStorage.getItem("authToken");
        const TEST_BASE_URL = process.env.REACT_APP_BASE_URL;
        const response = await fetch(
          `${TEST_BASE_URL}/upcoming-events`,
          {
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to load events");
        }

        const result = await response.json();
        const eventsArray = result?.data || [];

        const formatDatePretty = (dateStr) => {
          if (!dateStr) return "TBA";

          try {
            const cleaned = dateStr
              .replace(/(\d+)(st|nd|rd|th)/, "$1")
              .replace(",", "");
            const date = new Date(cleaned);
            if (isNaN(date)) return "TBA";

            const day = date.getDate();
            const months = [
              "Jan",
              "Feb",
              "Mar",
              "Apr",
              "May",
              "Jun",
              "Jul",
              "Aug",
              "Sep",
              "Oct",
              "Nov",
              "Dec",
            ];
            const month = months[date.getMonth()];
            const year = date.getFullYear();

            const getSuffix = (n) => {
              if (n > 3 && n < 21) return "th";
              switch (n % 10) {
                case 1:
                  return "st";
                case 2:
                  return "nd";
                case 3:
                  return "rd";
                default:
                  return "th";
              }
            };

            const suffix = getSuffix(day);
            return `${day}${suffix} ${month}, ${year}`;
          } catch {
            return "TBA";
          }
        };

        const formatted = eventsArray
          .map((item) => {
            const parsedDate = formatDatePretty(item.date);
            if (!parsedDate) return null;

            return {
              id: item.id,
              slug: item.slug,
              title: item.title,
              description: item.description,
              date: parsedDate,
              venue: item.venue,
              thumbnail: item.thumbnail,
              backgroundColor: "#0045E8",
            };
          })
          .filter(Boolean);

        setEvents(formatted);
      } catch (err) {
        console.error(" Error fetching events:", err);
        setError("Unable to load events. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  return (
    <section className="dashboard">
      <SideBar isOpen={sidebarOpen} />
      <div className="body-content">
        <TopBar onToggleSidebar={toggleSidebar} />

        <div className="bottom-content">
          <div className="row">
            <div className="col-md-8">
              <div className="calendar">
                <h5>Church's Events Calendar</h5>
                <hr />
                {loading ? (
                  <p>Loading events...</p>
                ) : error ? (
                  <p className="text-danger">{error}</p>
                ) : (
                  <FullCalendar
                    plugins={[dayGridPlugin, interactionPlugin]}
                    initialView="dayGridMonth"
                    events={events.map((event) => ({
                      id: event.id,
                      title: event.title,
                      start: toISODate(event.date || event.start_date),
                      backgroundColor: event.backgroundColor,
                      borderColor: event.backgroundColor,
                      extendedProps: {
                        description: event.description,
                        slug: event.slug,
                        thumbnail: event.thumbnail,
                      },
                    }))}
                    eventClick={(info) =>
                      setSelectedEvent({
                        id: info.event.id,
                        slug: info.event.extendedProps.slug,
                        title: info.event.title,
                        description: info.event.extendedProps.description,
                        thumbnail: info.event.extendedProps.thumbnail,
                        color: info.event.backgroundColor,
                      })
                    }
                    height="100%"
                  />
                )}
              </div>
            </div>

            <div className="col-md-4 col">
              <div className="calendar-event">
                {selectedEvent ? (
                  <>
                    {selectedEvent.thumbnail ? (
                      <img
                        src={
                          selectedEvent.thumbnail.startsWith("http")
                            ? selectedEvent.thumbnail
                            : `https://rccgbournemouth.org${
                                selectedEvent.thumbnail.startsWith("/") ? "" : "/"
                              }${selectedEvent.thumbnail}`
                        }
                        alt={selectedEvent.title}
                        width="100%"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = "none";
                          e.target.nextSibling.style.display = "flex";
                        }}
                      />
                    ) : null}
                    <div
                      className="placeholderimage"
                      style={{ display: selectedEvent.thumbnail ? "none" : "flex" }}
                    >
                      <p>{selectedEvent.title || "Image not available"}</p>
                    </div>

                    <h5 className="event-title">
                      <span
                        style={{
                          display: "inline-block",
                          width: "10px",
                          height: "10px",
                          borderRadius: "50%",
                          backgroundColor: selectedEvent.color,
                          marginRight: "8px",
                        }}
                      ></span>
                      {selectedEvent.title}
                    </h5>

                    <p className="event-description">
                      {selectedEvent.description}
                    </p>
                    <div className="white-buttons">
                      <a href={`/eventdetails/${selectedEvent.slug}`}>
                        See Details
                      </a>
                      <button
                        onClick={() => setRsvpOpen(true)}
                        className="text-dark"
                        style={{
                          cursor: "pointer",
                          background: "none",
                          border: "none",
                          color: "#000",
                        }}
                      >
                        RSVP
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="emptyevent">
                    <img src={add} alt="add event" />
                    <p>
                      Click on an event on the calendar to view the available
                      events
                    </p>
                  </div>
                )}
              </div>

              <div className="fasting">
                <div className="fallback-text">
                  <p className="text-muted mb-0"></p>
                </div>
                <div className="upcomingfasting">
                  <h6>
                    Church Fasting <br /> Periods
                  </h6>
                  <span>
                    See More{" "}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="11"
                      viewBox="0 0 12 11"
                      fill="none"
                    >
                      <path
                        d="M9.75 1.75L1.5 10"
                        stroke="#0045E8"
                        strokeWidth="1.125"
                        strokeLinecap="round"
                      />
                      <path
                        d="M5.25 1H9.75C10.1036 1 10.2803 1 10.3902 1.10983C10.5 1.21967 10.5 1.39645 10.5 1.75V6.25"
                        stroke="#0045E8"
                        strokeWidth="1.125"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </div>
                <p>
                  <small>
                    Join us in fasting and prayer as we humble ourselves before
                    God
                  </small>
                </p>
              </div>
            </div>
          </div>

          <div className="upcoming-events">
            <div className="upcoming">
              <h5>Upcoming Events</h5>
            </div>

            <div className="row singlesrow">
              {loading ? (
                <p>Loading upcoming events...</p>
              ) : error ? (
                <p className="text-danger">{error}</p>
              ) : events.length > 0 ? (
                events.slice(0, 3).map((event) => (
                  <div className="col-md-4" key={event.id}>
                    <div className="upcoming-card">
                      {event.thumbnail ? (
                        <img
                          src={
                            event.thumbnail.startsWith("http")
                              ? event.thumbnail
                              : `https://rccgbournemouth.org${
                                  event.thumbnail.startsWith("/") ? "" : "/"
                                }${event.thumbnail}`
                          }
                          alt={event.title}
                          width="100%"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = "none";
                            e.target.nextSibling.style.display = "flex";
                          }}
                        />
                      ) : null}
                      <div
                        className="fallback-text"
                        style={{ display: event.thumbnail ? "none" : "flex" }}
                      >
                        <p>{event.title || "Image not available"}</p>
                      </div>
                      <h6>{event.title}</h6>
                      <p className="text-white">{event.description}</p>
                      <hr />
                      <div className="datediv">
                        <div>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="17"
                            height="17"
                            viewBox="0 0 22 22"
                            fill="none"
                          >
                            <path
                              d="M17 1V3M5 1V3"
                              stroke="white"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M10.9955 12H11.0045M10.9955 16H11.0045M14.991 12H15M7 12H7.00897M7 16H7.00897"
                              stroke="white"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M2.5 7H19.5"
                              stroke="white"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M1.5 11.2432C1.5 6.88594 1.5 4.70728 2.75212 3.35364C4.00424 2 6.01949 2 10.05 2H11.95C15.9805 2 17.9958 2 19.2479 3.35364C20.5 4.70728 20.5 6.88594 20.5 11.2432V11.7568C20.5 16.1141 20.5 18.2927 19.2479 19.6464C17.9958 21 15.9805 21 11.95 21H10.05C6.01949 21 4.00424 21 2.75212 19.6464C1.5 18.2927 1.5 16.1141 1.5 11.7568V11.2432Z"
                              stroke="white"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          <span className="text-white">{event.date}</span>
                        </div>
                        <div>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="17"
                            height="17"
                            viewBox="0 0 20 22"
                            fill="none"
                          >
                            <path
                              d="M13.5 10C13.5 11.933 11.933 13.5 10 13.5C8.067 13.5 6.5 11.933 6.5 10C6.5 8.067 8.067 6.5 10 6.5C11.933 6.5 13.5 8.067 13.5 10Z"
                              stroke="white"
                              strokeWidth="1.5"
                            />
                            <path
                              d="M10 1C14.8706 1 19 5.03298 19 9.92577C19 14.8965 14.8033 18.3847 10.927 20.7567C10.6445 20.9162 10.325 21 10 21C9.67495 21 9.35549 20.9162 9.073 20.7567C5.2039 18.3616 1 14.9137 1 9.92577C1 5.03298 5.12944 1 10 1Z"
                              stroke="white"
                              strokeWidth="1.5"
                            />
                          </svg>
                          <span className="text-white">{event.venue}</span>
                        </div>
                      </div>
                      <div className="whitebuttons">
                        <a href={`/eventdetails/${event.slug}`}>See Details</a>
                        <button
                          onClick={() => {
                            setSelectedEvent(event);
                            setRsvpOpen(true);
                          }}
                          className="text-dark"
                          style={{
                            cursor: "pointer",
                            background: "none",
                            border: "none",
                            padding: 0,
                          }}
                        >
                          RSVP
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p>No upcoming events found.</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <RSVPModal
        isOpen={rsvpOpen}
        onClose={() => setRsvpOpen(false)}
        eventId={selectedEvent?.id}
      />
    </section>
  );
}